import React, { useEffect, useState } from "react";
import { Interview } from "../models/Interview";
import {
  calculateDaysBetweenDates,
  convertToLocalDateTime,
} from "../utilities/DateUtil";
import FeedbackStatusComponent from "../components/FeedbackStatusComponent";
import { FeedbackStatus } from "../models/FeedbackStatus";
import { fetchQuestionAnalysisList } from "../services/QuestionAnalysisService";
import {
  getActiveInterviews,
  getSubmittedInterviews,
} from "../services/InterviewService";
import { useLocation } from "react-router-dom";
import { DaysUntilAudioDeletion } from "../components/DaysUntilAudioDeletion";
import {
  Center,
  Container,
  Flex,
  Group,
  Input,
  Loader,
  Pagination,
  Paper,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";

interface ActiveInterviewProps {
  submittedInterviews: boolean;
}

const ActiveInterviews = ({ submittedInterviews }: ActiveInterviewProps) => {
  const location = useLocation();

  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageNum, setPageNum] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [feedbackLoading, setFeedbackLoading] = useState<boolean>(false);
  const [feedbackStatuses, setFeedbackStatuses] = useState<FeedbackStatus[]>(
    [],
  );

  const theme = useMantineTheme();

  useEffect(() => {
    const fetchInterviews = async () => {
      try {
        if (submittedInterviews) {
          const interviews: Interview[] = await getSubmittedInterviews(
            setLoading,
            pageNum,
            setTotalPages,
          );
          setInterviews(interviews);
        } else {
          const interviews: Interview[] = await getActiveInterviews(
            setLoading,
            pageNum,
            setTotalPages,
          );
          setInterviews(interviews);
        }
      } catch (error: any) {
        console.error(error.message);
      }
    };

    fetchInterviews();
  }, [pageNum, location.pathname, submittedInterviews]);

  const navigateExternal = (target: string) => {
    window.open(target, "_blank", "noreferrer");
  };

  const fetchQuestionAnalysisData = async (interview: Interview) => {
    try {
      return await fetchQuestionAnalysisList(
        interview.interviewId,
        setFeedbackLoading,
      );
    } catch (error: any) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    const updateFeedbackStatuses = async () => {
      const updatedStatuses: FeedbackStatus[] = [];
      for (const interview of interviews) {
        const data = await fetchQuestionAnalysisData(interview);
        const feedbackStatus: FeedbackStatus = {
          interviewId: interview.interviewId,
          status: interview.feedbackSubmitted
            ? "submitted"
            : Array.isArray(data) && data.length > 0
              ? "in progress"
              : "not started",
        };
        updatedStatuses.push(feedbackStatus);
      }
      setFeedbackStatuses(updatedStatuses);
    };

    updateFeedbackStatuses();
  }, [interviews]);

  return (
    <Container fluid w="100%">
      <Flex direction="column" w="100%">
        <Paper p="lg" shadow="lg">
          <Group justify="space-between">
            <Text component="h4" fw={700} size="lg">
              {submittedInterviews
                ? "Submitted Interviews"
                : "Active Interviews"}
            </Text>
            <Group>
              <Pagination
                value={pageNum}
                onChange={setPageNum}
                total={totalPages}
                withEdges
              />
              <Input
                px="lg"
                w="100"
                type="number"
                value={pageNum.toString()}
                onChange={(e) => {
                  if (
                    Number(e.target.value) >= 0 &&
                    Number(e.target.value) <= totalPages
                  ) {
                    setPageNum(Number(e.target.value));
                  }
                }}
              />
            </Group>
            <Text component="h4">
              {submittedInterviews ? "Updated Date" : "Created Date"} / Feedback
              Status
            </Text>
          </Group>
        </Paper>
        {loading ? (
          <Center h="100%">
            <Loader key="loader" pt="200" size="xl" />
          </Center>
        ) : (
          <Flex direction="column">
            {interviews.map((interview, index) => {
              const daysSinceCreation = calculateDaysBetweenDates(
                new Date(),
                new Date(interview.createdAt),
              );

              return (
                <UnstyledButton key={interview.interviewId}>
                  <Paper
                    shadow="lg"
                    p="lg"
                    mb="sm"
                    mt={index === 0 ? "sm" : ""}
                    onClick={() => {
                      navigateExternal(
                        `/feedback?interviewId=${interview.interviewId}`,
                      );
                    }}
                    bg={daysSinceCreation > 180 ? theme.colors.red[2] : ""}
                  >
                    <Group justify="space-between">
                      <Text>
                        {interview.jobTitle} at {interview.companyName}
                      </Text>
                      <Group>
                        {submittedInterviews && (
                          <Text me="sm">
                            Reviewer:{" "}
                            {interview.reviewer
                              ? interview.reviewer.username
                              : "No reviewer found"}
                          </Text>
                        )}
                        <Text>
                          {submittedInterviews
                            ? convertToLocalDateTime(interview.updatedAt)
                            : convertToLocalDateTime(interview.createdAt)}
                        </Text>
                        <FeedbackStatusComponent
                          feedbackLoading={feedbackLoading}
                          interview={interview}
                          feedbackStatuses={feedbackStatuses}
                        />
                      </Group>
                    </Group>
                    <Group mt="sm" justify="flex-end">
                      <DaysUntilAudioDeletion
                        dateOfCreation={new Date(interview.createdAt)}
                      />
                    </Group>
                  </Paper>
                </UnstyledButton>
              );
            })}
          </Flex>
        )}
      </Flex>
    </Container>
  );
};

export default ActiveInterviews;
